import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
} from "@mui/material";
import { OfferLetterFieldsHelper, UpdateOfferLetterTemplateHelper } from "../../helper/AtsFormTemplateHelper";
import OfferLetterDocumentEditor, { parseOfferLetterContent } from "./editor/OfferLetterDocumentEditor";

const General = ({
  activeStep,
  setActiveStep,
  data,
  setData,
  letterHeadData,
  companylogo,
  editorSubStep = "general",
  isStepLoading,
  setIsStepLoading,
}) => {
  const { letterHeadData: reduxLetterHead } = useSelector(
    (state) => state?.AncillaryReducer?.LetterHeadReducer || {}
  );
  const currentLetterHead = letterHeadData || (Array.isArray(reduxLetterHead) ? reduxLetterHead[0] : reduxLetterHead);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const updateLoading = (val) => {
    setLoading(val);
    if (setIsStepLoading) setIsStepLoading(val);
  };
  const [errors, setErrors] = useState({});
  const [offerletter,] = useState({
    description: "",
  });
  const [description, setDescription] = useState(data?.offer_letter_content || "");
  const documentEditorRef = useRef(null);

  useEffect(() => {
    if (data?.offer_letter_content && data.offer_letter_content !== description) {
      setDescription(data.offer_letter_content);
    }
  }, [data?.offer_letter_content]);

  useEffect(() => {
    setDescription(data?.offer_letter_content || "");
    OfferLetterFieldsHelper({
      offer_letter_template_id: data?.offer_letter_template_id,
    }).then((res) => {
      if (res?.data?.statuscode === 200) {
        let arr = [];
        res?.data?.data?.forEach((temp) => {
          const isFieldNumeric = /^field\d+$/i.test(temp?.name || "");
          const varName = isFieldNumeric && temp?.label
            ? temp.label.toLowerCase().trim().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "")
            : (temp?.name || temp?.label);
          arr.push({ ...temp, id: varName, name: varName, display: temp?.label || varName });
        });
        setMentionUsers(arr);
      }
    });
    //eslint-disable-next-line
  }, []);

  const validate = (fieldValues, description) => {
    let temp = { ...errors };
    if ("description" in fieldValues) {
      temp.description =
        !description || description === "" || description === "<p><br></p>"
          ? "Description is required"
          : "";
    }
    setErrors({
      ...temp,
    });
    return Object.values(temp).every((x) => x === "");
  };

  const handleBack = async () => {
    let currentBytes = data?.docBytes;
    let currentPayload = data?.offer_letter_content;
    let currentSelectedKeys = data?.selectedKeys;
    let currentCustomSubtotals = data?.customSubtotals;

    if (documentEditorRef.current?.saveDocx) {
      const saved = await documentEditorRef.current.saveDocx();
      if (saved) {
        currentBytes = saved.bytes;
        currentPayload = saved.packageJson;
        currentSelectedKeys = saved.selectedKeys;
        currentCustomSubtotals = saved.customSubtotals;
      }
    }

    const parsed = parseOfferLetterContent(currentPayload || data?.offer_letter_content);
    const activeMode = data?.header_footer_mode || parsed?.header_footer_mode || "OUR_HEADER_FOOTER";

    setData((prev) => ({
      ...prev,
      header_footer_mode: activeMode,
      docBytes: currentBytes || prev?.docBytes,
      offer_letter_content: currentPayload || prev?.offer_letter_content,
      selectedKeys: currentSelectedKeys || prev?.selectedKeys,
      customSubtotals: currentCustomSubtotals || prev?.customSubtotals,
    }));
    setActiveStep(activeStep - 1);
  };

  const onSubmit = async () => {
    updateLoading(true);
    let currentBytes = data?.docBytes;
    let currentPayload = data?.offer_letter_content;
    let currentSelectedKeys = data?.selectedKeys;
    let currentCustomSubtotals = data?.customSubtotals;

    try {
      if (documentEditorRef.current?.saveDocx) {
        const saved = await documentEditorRef.current.saveDocx();
        if (saved) {
          currentBytes = saved.bytes;
          currentPayload = saved.packageJson;
          currentSelectedKeys = saved.selectedKeys;
          currentCustomSubtotals = saved.customSubtotals;
          setDescription(saved.packageJson);
        }
      }

      if (!currentPayload) {
        currentPayload = JSON.stringify({
          version: 2,
          format: "docx-package",
          html: "<p>Offer Letter Document Content</p>",
        });
      }

      const parsed = parseOfferLetterContent(currentPayload);
      const activeMode = data?.header_footer_mode ?? parsed?.header_footer_mode ?? "OUR_HEADER_FOOTER";

      if (validate(offerletter, currentPayload)) {
        if (data?.offer_letter_template_id) {
          await UpdateOfferLetterTemplateHelper({
            ...data,
            offer_letter_template_id: data?.offer_letter_template_id,
            offer_letter_template_name: data?.offer_letter_template_name,
            offer_letter_template_fields: typeof data?.offer_letter_template_fields === "string"
              ? data?.offer_letter_template_fields
              : JSON.stringify(data?.offer_letter_template_fields),
            offer_letter_content: currentPayload,
            is_letterhead: activeMode === "LETTERHEAD",
          }).then((res) => {
            if (res?.data?.statuscode === 200) {
              const nextData = {
                ...(res?.data?.data?.[0] || data),
                is_letterhead: activeMode === "LETTERHEAD",
                header_footer_mode: activeMode,
                docBytes: currentBytes,
                offer_letter_content: currentPayload,
                selectedKeys: currentSelectedKeys || data?.selectedKeys,
                customSubtotals: currentCustomSubtotals || data?.customSubtotals,
              };
              setData(nextData);
              setActiveStep(activeStep + 1);
            } else {
              setActiveStep(activeStep + 1);
              setData((prev) => ({
                ...prev,
                is_letterhead: activeMode === "LETTERHEAD",
                header_footer_mode: activeMode,
                docBytes: currentBytes || prev?.docBytes,
                offer_letter_content: currentPayload || prev?.offer_letter_content,
                selectedKeys: currentSelectedKeys || prev?.selectedKeys,
                customSubtotals: currentCustomSubtotals || prev?.customSubtotals,
              }));
            }
          }).catch(() => {
            setActiveStep(activeStep + 1);
            setData((prev) => ({
              ...prev,
              is_letterhead: activeMode === "LETTERHEAD",
              header_footer_mode: activeMode,
              docBytes: currentBytes || prev?.docBytes,
              offer_letter_content: currentPayload || prev?.offer_letter_content,
              selectedKeys: currentSelectedKeys || prev?.selectedKeys,
              customSubtotals: currentCustomSubtotals || prev?.customSubtotals,
            }));
          });
        } else {
          const nextData = {
            ...data,
            is_letterhead: activeMode === "LETTERHEAD",
            header_footer_mode: activeMode,
            docBytes: currentBytes,
            offer_letter_content: currentPayload,
            selectedKeys: currentSelectedKeys || data?.selectedKeys,
            customSubtotals: currentCustomSubtotals || data?.customSubtotals,
          };
              setData(nextData);
              setActiveStep(activeStep + 1);
        }
      }
    } catch (err) {
      console.error("Error saving document:", err);
    } finally {
      updateLoading(false);
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} md={12} padding={0}>
          <Box sx={{ width: "100%" }}>
            <OfferLetterDocumentEditor
              ref={documentEditorRef}
              value={description}
              setValue={setDescription}
              onChange={(newContent) => {
                setDescription(newContent);
                setData((prev) => ({ ...prev, offer_letter_content: newContent }));
              }}
              docBytes={data?.docBytes}
              selectedKeys={data?.selectedKeys}
              customSubtotals={data?.customSubtotals}
              header_footer_mode={data?.header_footer_mode || (data?.is_letterhead ? "LETTERHEAD" : "OUR_HEADER_FOOTER")}
              is_letterhead={data?.is_letterhead}
              letterHeadData={letterHeadData}
              onDocBytesChange={(bytes) => {
                setData((prev) => ({ ...prev, docBytes: bytes }));
              }}
              mentionUsers={mentionUsers}
              companylogo={companylogo}
              editorSubStep={editorSubStep}
              editorTitle={editorSubStep === "ctc" ? "CTC Detail Configuration" : "Offer Letter Customization"}
              headerTitle={data?.header || currentLetterHead?.letterhead_header}
              footerText={data?.footer || currentLetterHead?.letterhead_footer}
              isStepLoading={isStepLoading || loading}
            />
            {errors?.description && (
              <Typography
                sx={{
                  color: "red",
                  paddingTop: 1,
                  textAlign: "center",
                }}
              >
                {errors?.description}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "6px",
              padding: "0px 10px 4px 10px",
            }}
          >
            <Button
              variant="contained"
              className="mpl-primary-btn"
              onClick={handleBack}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              variant="contained"
              className="mpl-primary-btn"
              onClick={onSubmit}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              {editorSubStep === "general" ? (data?.is_configure_ctc === false ? "Next: Preview" : "Next: CTC Configuration") : "Next"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default General;